/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */

/**
 * Helper for table generation
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.0.0
 */
import { PureComponent } from 'react';

import { guardComponentType } from '@modules/role/reducer/guardReducer';

import Columns from './components/TableColumns/Columns';
import type { ITableField } from './reducers/tableReducer';
import { ColumnFieldType, ColumnTranslationType } from './types/columnTypes';

export enum ETableColumnUtilHideFilters {
    EMPTY = 'empty',
    STUB = 'stub',
}

export interface ITableColumnUtilProps<Field extends ColumnFieldType, Translation extends ColumnTranslationType> {
    cellConfig: { [Key in Field]: number };
    columnNames: Field[];
    componentsFeature: guardComponentType;
    fields?: Array<ITableField<Field>>;
    getColumnCellType: (name: Field) => string;
    getColumnFilterType: (name: Field) => string;
    getColumnFixedType: (name: Field) => string | boolean;
    hideFiltersType?: ETableColumnUtilHideFilters;
    isDisableFilter?: boolean;
    isDisableSort?: boolean;
    isTableResizeEnabled: boolean;
    translation: Translation;
}

class TableColumnUtil<Field extends ColumnFieldType, Translation extends ColumnTranslationType>
    extends PureComponent<ITableColumnUtilProps<Field, Translation>> {

    private readonly tableProps: ITableColumnUtilProps<Field, Translation>;

    constructor(props: ITableColumnUtilProps<Field, Translation>) {
        super(props);
        this.tableProps = props;
    }

    customizeTableColumns = () => {
        const { fields, columnNames } = this.tableProps;
        return fields && fields.length
            ? fields
                .filter((field: ITableField<Field>) => field.checked)
                .sort((a: ITableField<Field>, b: ITableField<Field>) => a.order - b.order)
                .map((field: ITableField<Field>) => this.getTableColumn(field.name as Field, field))
            : columnNames.map((column: Field) => this.getTableColumn(column));
    };

    private getTableColumn = (name: Field, field?: ITableField<Field>) => {
        const {
            cellConfig,
            componentsFeature,
            getColumnCellType,
            getColumnFilterType,
            getColumnFixedType,
            hideFiltersType,
            isDisableFilter,
            isDisableSort,
            translation,
        } = this.tableProps;
        return Columns({
            cellType: getColumnCellType(name),
            componentsFeature,
            filterType: getColumnFilterType(name),
            fixed: getColumnFixedType(name),
            hideFiltersType,
            isDisableFilter,
            isDisableSort,
            label: field?.label,
            name,
            resizable: field ? (this.props.isTableResizeEnabled && field.resizable) : true,
            tooltipMsg: field ? field.description : '',
            translation: translation,
            width: field ? field.size : cellConfig[name],
        });
    };

}

export default TableColumnUtil;
